import { z } from 'zod'
import { TaskActionSchema, TaskTypeSchema } from '../program/task'
import { ProgramTaskExpirationSchema, ProgramTaskIdSchema } from '../program/programTask'
import { PROGRAM_TASK_CRITERIA_TYPES, ProgramTaskCriteriaSchema } from '../program/criteria'
import { idSchemaV1, idSchemaV2, isId, ProgramIdSchema, UserIdSchema, type UserId } from '../ids'
import { TinyUserSchema } from '../entity'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const TaskIdSchema = idSchemaV1('Task')
export type TaskId = z.infer<typeof TaskIdSchema>
export const isTaskId = isId(TaskIdSchema)
export type WithTaskId = { taskId: TaskId }

export const TASK_STATUSES = ['Created', 'Submitted', 'Completed', 'Rejected'] as const
export const TaskStatusSchema = z.enum(TASK_STATUSES)

export const isTaskStatus = (val: unknown): val is TaskStatus => TaskStatusSchema.safeParse(val).success
export type TaskStatus = z.infer<typeof TaskStatusSchema>

export const ManagerTaskIdSchema = idSchemaV2('MT')
export type ManagerTaskId = z.infer<typeof ManagerTaskIdSchema>
export const isManagerTaskId = isId(ManagerTaskIdSchema)

export const TaskOriginIdSchema = z.union([ManagerTaskIdSchema, ProgramTaskIdSchema])
export type TaskOriginId = z.infer<typeof TaskOriginIdSchema>
export const isTaskOriginId = (val: unknown): val is TaskOriginId => TaskOriginIdSchema.safeParse(val).success

export const UnsavedTaskSchema = z.object({
  ownerId: UserIdSchema,
  targetId: UserIdSchema.optional(),
  type: TaskTypeSchema,
  subject: z.string().describe('short sentence case title for this task'),
  body: z
    .string()
    .optional()
    .describe("longer explanation for this task (what, why, and how). Displays below the title so don't repeat that."),
  dueDate: z.number().optional().meta({ description: 'When the task is due, in UTC epoch milliseconds' }),
  required: z.boolean().describe('must this task be completed today?'),
  rejectable: z.boolean().optional().describe('can this task be rejected? (default: true)'),
  closable: z.boolean().optional().describe('can this task be marked as completed by the user?'),
  actions: z.array(TaskActionSchema).optional(),
  priority: z.number().describe('priority to describe the importance (and urgency) of a task'),
  originTaskId: TaskOriginIdSchema.optional(),
  programId: ProgramIdSchema.optional(),
  criteria: ProgramTaskCriteriaSchema.optional(),
  /** @deprecated - use nextTaskIds */
  nextTaskId: TaskIdSchema.optional().meta({ deprecated: true }),
  nextTaskIds: TaskIdSchema.array().optional(),
  openSince: z.number().optional().meta({ description: 'When the task opened, in UTC epoch milliseconds' }),
  expiration: ProgramTaskExpirationSchema.optional(),
  forceCheckProgram: z.boolean().optional().meta({
    description: 'should this task check program completion regardless of whether it is completed',
    audience: 'none',
  }),
})

export type UnsavedTask = z.infer<typeof UnsavedTaskSchema>

export const TaskDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedTaskSchema.required({ actions: true }).shape,
  taskId: TaskIdSchema,
  from: TinyUserSchema.optional().describe('populated asynchronously from fromId during transformation'),
  owner: TinyUserSchema.optional().describe('populated asynchronously from ownerId during transformation'),
  status: TaskStatusSchema,
  completedAt: z.number().optional().meta({ description: 'When the task was completed, in UTC epoch milliseconds' }),
  ownerFeedback: z.string().optional(),
  snoozeUntil: z.number().optional().meta({ description: 'When the snooze ends, in UTC epoch milliseconds' }),
  progress: z
    .number()
    .optional()
    .describe(
      'For tasks that have criteria to complete (i.e. Pitch 3 houses) the progress is how many have been completed'
    ),
})

export type TaskData = z.infer<typeof TaskDataSchema>

export type TaskImport = {
  raw: string
  name?: string
  owner?: UserId
  status?: TaskStatus
  taskId?: TaskId
  progress?: number
}

export const NORMALIZED_TASK_CRITERIA_TYPES = [...PROGRAM_TASK_CRITERIA_TYPES, 'quickLink'] as const
export const NormalizedTaskCriteriaTypeSchema = z.enum(NORMALIZED_TASK_CRITERIA_TYPES)
export type NormalizedTaskCriteriaType = z.infer<typeof NormalizedTaskCriteriaTypeSchema>
